/**
 * Audio Workspace — shell component with inline tab bar (Scratch/Record/Import).
 */

import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useUiStore } from '@/store/uiStore.ts';
import { trackEvent } from '@/telemetry/trackEvent.ts';
import { useAudioStore } from '@/store/audioStore.ts';
import { useProjectStore } from '@/store/projectStore.ts';
import { usePlaybackStore } from '@/store/playbackStore.ts';
import { useUndoStore } from '@/store/undoStore.ts';
import { stashBlob } from '@/previs/audioBlobStore.ts';
import { resolveClipPlacement } from '@/store/projectDerived.ts';
import { LANE_COLORS } from '@/audio/audioConstants.ts';
import { markClipAsUnityKnown } from '@/bridge/audioSyncBridge.ts';
import { capabilities } from '@/capabilities.ts';
import { RecordPanel } from './RecordPanel.tsx';
import { ImportPanel } from './ImportPanel.tsx';
import { PreviewBar } from './PreviewBar.tsx';
import { ScratchPanel } from './ScratchPanel.tsx';
import type { StagedAudio } from './PreviewBar.tsx';
import type { RecordResult } from './RecordPanel.tsx';
import type { ImportResult } from './ImportPanel.tsx';
import type { AudioLaneIndex } from '@/store/uiStore.ts';
import styles from './AudioWorkspace.module.css';

type V2Tab = 'scratch' | 'record' | 'import';

const LANE_LABEL_KEYS = ['laneDialog', 'laneMusic', 'laneSfx', 'laneVoiceover'];
const LANE_COLORS_LIST = ['#4a90d9', '#d94a8a', '#4ad97a', '#d9a54a'];

const TABS: { id: V2Tab; labelKey: string }[] = [
  { id: 'scratch', labelKey: 'tabScratch' },
  { id: 'record', labelKey: 'tabRecord' },
  { id: 'import', labelKey: 'tabImport' },
];

export function AudioWorkspace() {
  const { t } = useTranslation('audioWorkspace');
  const closeAudioWorkspace = useUiStore(s => s.closeAudioWorkspace);

  // Fire telemetry when the audio workspace mounts
  useEffect(() => {
    trackEvent('ai_panel_opened', { panel: 'audio' });
  }, []);
  const targetLane = useUiStore(s => s.audioWorkspaceTargetLane);
  const setTargetLane = useUiStore(s => s.setAudioWorkspaceTargetLane);
  const [activeTab, setActiveTab] = useState<V2Tab>('scratch');
  const [stagedRecord, setStagedRecord] = useState<StagedAudio | null>(null);
  const [stagedImport, setStagedImport] = useState<StagedAudio | null>(null);
  const [scratchBusy, setScratchBusy] = useState(false);
  const [recordBusy, setRecordBusy] = useState(false);

  const currentStaged = activeTab === 'record' ? stagedRecord : activeTab === 'import' ? stagedImport : null;

  const handleRecordStage = useCallback((result: RecordResult | ImportResult, label: string) => {
    setStagedRecord({
      audioBuffer: result.audioBuffer,
      peaks: result.peaks,
      rawData: result.rawData,
      duration: result.duration,
      label,
      mimeType: result.mimeType,
      ...('clipId' in result && result.clipId != null ? { clipId: result.clipId } : {}),
    });
  }, []);

  const handleImportStage = useCallback((result: RecordResult | ImportResult, label: string) => {
    setStagedImport({
      audioBuffer: result.audioBuffer,
      peaks: result.peaks,
      rawData: result.rawData,
      duration: result.duration,
      label,
      mimeType: result.mimeType,
    });
  }, []);

  const handleAddAudio = useCallback(() => {
    if (!currentStaged) return;

    const currentTime = usePlaybackStore.getState().currentTime;
    const lane = useUiStore.getState().audioWorkspaceTargetLane;

    useUndoStore.getState().pushSnapshot();

    const proj = useProjectStore.getState().project;
    const placement = proj ? resolveClipPlacement(currentTime, proj) : { shotId: 0, shotOffset: currentTime };

    if (capabilities.embeddedRecording && currentStaged.clipId != null) {
      // Embedded mode: clip already in Unity's SQLite.
      // Mark as unity-known so audioSyncBridge skips blob transfer.
      markClipAsUnityKnown(currentStaged.clipId);

      useAudioStore.getState().addAudioClip({
        id: currentStaged.clipId,
        label: currentStaged.label,
        shotId: placement.shotId,
        shotOffset: placement.shotOffset,
        duration: currentStaged.duration,
        volume: 0.75,
        bufferOffset: 0,
        audioBuffer: null,
        mimeType: currentStaged.mimeType,
        lane,
        isMuted: false,
        fadeInTime: 0,
        fadeOutTime: 0,
        color: LANE_COLORS[lane],
        peaks: currentStaged.peaks,
      }, { pushUndo: false });
    } else {
      // Standalone mode: existing path
      const newId = useProjectStore.getState()._allocId();
      stashBlob(newId, currentStaged.rawData);

      useAudioStore.getState().addAudioClip({
        id: newId,
        label: currentStaged.label,
        shotId: placement.shotId,
        shotOffset: placement.shotOffset,
        duration: currentStaged.duration,
        volume: 0.75,
        bufferOffset: 0,
        audioBuffer: currentStaged.audioBuffer,
        mimeType: currentStaged.mimeType,
        lane,
        isMuted: false,
        fadeInTime: 0,
        fadeOutTime: 0,
        color: LANE_COLORS[lane],
        peaks: currentStaged.peaks,
      }, { pushUndo: false });
    }

    // Clear only the current tab's staged audio
    if (activeTab === 'record') setStagedRecord(null);
    else if (activeTab === 'import') setStagedImport(null);
  }, [currentStaged, activeTab]);

  return (
    // Theme-aware since the 20260803 light-mode color audit (all chrome tokenized in
    // AudioWorkspace.module.css) — the force-dark data-theme-scope stopgap is retired.
    <div className={styles.workspace}>
      <div className={styles.header}>
        <div className={styles.tabs}>
          {TABS.map(tab => {
            const isDisabled =
              (tab.id !== 'scratch' && scratchBusy) ||
              (tab.id !== 'record' && recordBusy);
            return (
              <button
                key={tab.id}
                className={`${styles.tab} ${activeTab === tab.id ? styles.tabActive : ''}`}
                onClick={() => !isDisabled && setActiveTab(tab.id)}
                disabled={isDisabled}
              >
                {t(tab.labelKey)}
              </button>
            );
          })}
        </div>
        <button
          className={styles.closeBtn}
          onClick={closeAudioWorkspace}
          title={t('closeAudioWorkspaceTitle')}
        >
          &#x24E7;
        </button>
      </div>
      <div className={activeTab === 'scratch' ? styles.body : styles.bodyCentered}>
        <div style={{ display: activeTab === 'scratch' ? 'contents' : 'none' }}>
          <ScratchPanel onBusyChange={setScratchBusy} />
        </div>
        <div style={{ display: activeTab === 'record' ? 'contents' : 'none' }}>
          <RecordPanel onStage={handleRecordStage} onBusyChange={setRecordBusy} />
        </div>
        <div style={{ display: activeTab === 'import' ? 'contents' : 'none' }}>
          <ImportPanel onStage={handleImportStage} />
        </div>
      </div>
      {currentStaged && (
        <PreviewBar
          staged={currentStaged}
          onAddAudio={handleAddAudio}
          hideLabel
          extraControls={
            <div className={styles.laneSelector}>
              <span className={styles.laneDot} style={{ background: LANE_COLORS_LIST[targetLane] }} />
              <select
                className={styles.laneSelect}
                value={targetLane}
                onChange={e => setTargetLane(Number(e.target.value) as AudioLaneIndex)}
              >
                {LANE_LABEL_KEYS.map((key, i) => (
                  <option key={key} value={i}>{t(key)}</option>
                ))}
              </select>
            </div>
          }
        />
      )}
    </div>
  );
}
